import React from "react";
import {
  MdEdit,
  MdDelete,
  MdMedicalServices,
  MdPersonPin,
  MdPeople,
  MdVisibility,
} from "react-icons/md";

const ServiceCard = ({
  service,
  onEdit,
  onDelete,
  onView,
  viewMode = "grid",
  getPhotoUrl,
  getStatusService,
}) => {
  const status = getStatusService(service);
  const getStatusColor = (status) => {
    switch (status) {
      case "Complet":
        return "bg-green-500";
      case "Partiel":
        return "bg-yellow-500";
      case "En attente":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const statusColorClass = getStatusColor(status);

  if (viewMode === "list") {
    return (
      <div className="mb-4 overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-300 hover:shadow-lg">
        <div className="flex flex-col md:flex-row">
          {service.photo ? (
            <div className="h-48 w-full md:h-auto md:w-64">
              <img
                src={getPhotoUrl(service.photo)}
                alt={service.nom}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10 md:h-auto md:w-64">
              <MdMedicalServices className="text-6xl text-[#2ca01c]/40" />
            </div>
          )}

          <div className="flex flex-1 flex-col p-5">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#333333]">
                {service.nom}
              </h2>
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
            </div>

            <p className="mb-4 line-clamp-2 text-gray-600">
              {service.description || "Aucune description fournie"}
            </p>

            <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg bg-[#f9f9f9] p-3 sm:grid-cols-2">
              <p className="flex items-center text-gray-700">
                <MdMedicalServices className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Pathologie:</span>
                <span className="ml-2 text-sm">{service.pathologie}</span>
              </p>
              {service.chefService && (
                <p className="flex items-center text-gray-700">
                  <MdPersonPin className="mr-2 text-[#7028a9]" />
                  <span className="font-medium">Chef:</span>
                  <span className="ml-2 text-sm">
                    {service.chefService.titre} {service.chefService.name}
                  </span>
                </p>
              )}
              {service.members && service.members.length > 0 && (
                <p className="flex items-center text-gray-700">
                  <MdPeople className="mr-2 text-[#7028a9]" />
                  <span className="font-medium">Équipe:</span>
                  <span className="ml-2 text-sm">
                    {service.members.length} médecin(s)
                  </span>
                </p>
              )}
            </div>

            <div className="mt-auto flex space-x-3">
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
                onClick={() => onView(service)}
              >
                <MdVisibility className="mr-1" /> Détails
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
                onClick={() => onEdit(service)}
              >
                <MdEdit className="mr-1" /> Modifier
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
                onClick={() => onDelete(service.id)}
              >
                <MdDelete className="mr-1" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-300 hover:shadow-xl">
      {service.photo ? (
        <div className="relative h-48 overflow-hidden">
          <img
            src={getPhotoUrl(service.photo)}
            alt={service.nom}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
          />
          <div className="from-black/60 to-transparent absolute bottom-0 left-0 w-full bg-gradient-to-t p-3">
            <span
              className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
            >
              {status}
            </span>
          </div>
        </div>
      ) : (
        <div className="relative flex h-48 items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10">
          <MdMedicalServices className="text-6xl text-[#2ca01c]/40" />
          <div className="from-black/60 to-transparent absolute bottom-0 left-0 w-full bg-gradient-to-t p-3">
            <span
              className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
            >
              {status}
            </span>
          </div>
        </div>
      )}

      <div className="p-5">
        <h2 className="mb-3 border-b border-gray-100 pb-2 text-xl font-semibold text-[#333333]">
          {service.nom}
        </h2>
        <p className="mb-5 line-clamp-3 text-gray-600">
          {service.description || "Aucune description fournie"}
        </p>

        <div className="mb-6 space-y-2 rounded-lg bg-[#f9f9f9] p-3">
          <p className="flex items-center text-gray-700">
            <MdMedicalServices className="mr-2 text-[#7028a9]" />
            <span className="font-medium">Pathologie:</span>
            <span className="ml-2">{service.pathologie}</span>
          </p>
          {service.chefService && (
            <p className="flex items-center text-gray-700">
              <MdPersonPin className="mr-2 text-[#7028a9]" />
              <span className="font-medium">Chef:</span>
              <span className="ml-2">
                {service.chefService.titre} {service.chefService.name}
              </span>
            </p>
          )}
          {service.members && service.members.length > 0 && (
            <p className="flex items-center text-gray-700">
              <MdPeople className="mr-2 text-[#7028a9]" />
              <span className="font-medium">Équipe:</span>
              <span className="ml-2">{service.members.length} médecin(s)</span>
            </p>
          )}
        </div>

        <div className="flex flex-col space-y-2">
          <button
            className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
            onClick={() => onView(service)}
          >
            <MdVisibility className="mr-1" /> Voir les détails
          </button>
          <div className="flex space-x-3">
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
              onClick={() => onEdit(service)}
            >
              <MdEdit className="mr-1" /> Modifier
            </button>
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
              onClick={() => onDelete(service.id)}
            >
              <MdDelete className="mr-1" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
